/**
 * Claim Detail — Einzelansicht einer Schadensmeldung / Nachforschung
 * mit Info-Cards, Timeline, Dokumenten, Status-Wechsel und Abschluss-Modal.
 * @page /claims/:id
 * @roles admin, manager
 * @i18n claims
 * @endpoints GET /api/claims/:id, PUT /api/claims/:id/status, PUT /api/claims/:id/assign,
 *            PUT /api/claims/:id/resolve, POST /api/claims/:id/notes,
 *            POST /api/claims/:id/documents, DELETE /api/claims/:id/documents/:docId
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useTabParam from '../../hooks/useTabParam';
import { useLanguage } from '../../context/LanguageContext';
import { PageHeader, Button } from '../../components/ui';
import BackLink from '../../components/BackLink';
import MailOriginalModal from '../../components/MailOriginalModal';
import { renderMailBody, hasOriginalHtml, getOriginalHtml } from '../../utils/mailRender';
import LinkedEntityList from '../../components/LinkedEntityList';
import LinkEntityModal from '../../components/LinkEntityModal';
import UniversalChatter from '../../components/UniversalChatter';
import RichTextEditor from '../../components/RichTextEditor';
import {
  User, Clock, Package, Truck, FileText, Upload,
  Send, CheckCircle2, XCircle, AlertTriangle, MessageSquare,
  Download, Trash2, X, ChevronLeft, ChevronRight, Image,
  ShieldAlert, RefreshCw, Edit3, Calendar, Hash, MapPin, BookOpen,
  Pencil, Check as CheckIcon, GitBranch,
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

// #1760 — Dokument-Download-URL (claim_documents ODER Nachrichten-Anhang via server-gelieferter download_url).
const docDownloadUrl = (doc) => `${API}${doc.download_url || `/api/claims/documents/${doc.id}/download`}`;

// #1161 — HTML-Escape Helper für Forward-Header
function escapeHtml(s) {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// #1757 — Flow-Klartext-Carrier (carrier_name_raw, z.B. "POST") auf claim_carriers mappen:
// exakter Match, sonst EINDEUTIGER Substring-Match (beide Richtungen); mehrdeutig → kein Prefill.
function matchCarrierByRawName(carriers, raw) {
  if (!raw) return null;
  const norm = s => String(s || '').trim().toLowerCase();
  const r = norm(raw);
  if (!r) return null;
  const exact = carriers.find(c => norm(c.name) === r);
  if (exact) return exact;
  const candidates = carriers.filter(c => norm(c.name).includes(r) || r.includes(norm(c.name)));
  return candidates.length === 1 ? candidates[0] : null;
}

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
  return fetch(`${API}${path}`, {
    ...opts,
    credentials: 'include',
    headers: { ...headers, ...opts.headers },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(async (r) => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || data.message || `HTTP ${r.status}`);
    return data;
  });
}

// ── Status config ──

const STATUS_CONFIG = {
  clarification:           { dot: 'bg-indigo-500',  bg: 'bg-indigo-500/10',  text: 'text-indigo-600 dark:text-indigo-400' },
  clarification_resolved:  { dot: 'bg-slate-400',   bg: 'bg-slate-500/10',   text: 'text-[var(--text-secondary,#4B5563)] dark:text-[var(--text-tertiary,#6B7280)]' },
  submitted:            { dot: 'bg-blue-500',    bg: 'bg-blue-500/10',    text: 'text-blue-600 dark:text-blue-400' },
  in_review:            { dot: 'bg-yellow-500',  bg: 'bg-yellow-500/10',  text: 'text-yellow-600 dark:text-yellow-400' },
  documents_missing:    { dot: 'bg-orange-500',  bg: 'bg-orange-500/10',  text: 'text-orange-600 dark:text-orange-400' },
  approved:             { dot: 'bg-cyan-500',    bg: 'bg-cyan-500/10',    text: 'text-cyan-600 dark:text-cyan-400' },
  at_carrier:           { dot: 'bg-primary-500',  bg: 'bg-primary-500/10',  text: 'text-primary-600 dark:text-primary-400' },
  waiting_for_carrier:  { dot: 'bg-gray-400',    bg: 'bg-gray-500/10',    text: 'text-[var(--text-secondary,#4B5563)] dark:text-[var(--text-tertiary,#6B7280)]' },
  carrier_inquiry:      { dot: 'bg-yellow-500',  bg: 'bg-yellow-500/10',  text: 'text-yellow-600 dark:text-yellow-400' },
  carrier_response:     { dot: 'bg-cyan-500',    bg: 'bg-cyan-500/10',    text: 'text-cyan-600 dark:text-cyan-400' },
  objection:            { dot: 'bg-orange-500',  bg: 'bg-orange-500/10',  text: 'text-orange-600 dark:text-orange-400' },
  resolved:             { dot: 'bg-emerald-500', bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400' },
  rejected:             { dot: 'bg-gray-400',    bg: 'bg-gray-500/10',    text: 'text-[var(--text-secondary,#4B5563)] dark:text-[var(--text-tertiary,#6B7280)]' },
};

// #1216 C1: Status frei wählbar — flache Liste statt State-Machine.
// Backend PUT /api/claims/:id/status akzeptiert jeden Wert aus dem DB-CHECK-Enum.
const ALL_STATUSES = [
  'submitted', 'in_review', 'documents_missing', 'approved',
  'at_carrier', 'waiting_for_carrier', 'carrier_inquiry', 'carrier_response',
  'objection', 'resolved', 'rejected',
];

const TIMELINE_ICONS = {
  created:           { Icon: ShieldAlert,    color: 'text-primary-500' },
  status_change:     { Icon: RefreshCw,      color: 'text-blue-500' },
  assigned:          { Icon: User,           color: 'text-cyan-500' },
  note:              { Icon: MessageSquare,  color: 'text-[var(--text-secondary,#4B5563)]' },
  document_uploaded: { Icon: Upload,         color: 'text-emerald-500' },
  document_deleted:  { Icon: Trash2,         color: 'text-red-500' },
  resolved:          { Icon: CheckCircle2,   color: 'text-emerald-500' },
  email_sent:        { Icon: Send,           color: 'text-primary-500' },
  email_received:    { Icon: FileText,       color: 'text-blue-500' },
};

const DOC_TYPE_ICONS = {
  damage_photo: Image,
  packaging_photo: Image,
  goods_value_proof: FileText,
  sales_invoice: FileText,
  carrier_correspondence: FileText,
  other: FileText,
};

function StatusBadge({ status, t }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.submitted;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-badge text-[12px] font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {t(`claims.status.${status}`) || status}
    </span>
  );
}

function TypeBadge({ type, t }) {
  // #1217 A1: 3 Typen — damage/investigation/content_missing
  if (type === 'damage') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-badge text-[12px] font-medium bg-red-500/10 text-red-600 dark:text-red-400">
        {t('claims.damage')}
      </span>
    );
  }
  if (type === 'content_missing') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-badge text-[12px] font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400">
        {t('claims.contentMissing')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-badge text-[12px] font-medium bg-blue-500/10 text-blue-600 dark:text-blue-400">
      {t('claims.investigation')}
    </span>
  );
}

function InfoRow({ label, value, mono = false }) {
  return (
    <div className="flex items-start justify-between py-2.5 border-b border-line last:border-0">
      <span className="text-[12px] text-content-secondary">{label}</span>
      <span className={`text-[13px] text-content font-medium text-right max-w-[60%] ${mono ? 'font-mono' : ''}`}>{value || '–'}</span>
    </div>
  );
}

// #1216 B1: Inline-Editable InfoRow für Kunde + Warenwert (Pencil-Pattern)
function EditableInfoRow({ label, value, displayValue, editor, onSave, saving, mono = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');

  useEffect(() => { setDraft(value ?? ''); }, [value, editing]);

  const handleSave = async () => {
    await onSave(draft);
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft(value ?? '');
    setEditing(false);
  };

  return (
    <div className="flex items-start justify-between py-2.5 border-b border-line last:border-0 gap-2">
      <span className="text-[12px] text-content-secondary shrink-0 pt-0.5">{label}</span>
      {editing ? (
        <div className="flex items-center gap-1.5 flex-1 justify-end">
          {editor({ value: draft, setValue: setDraft, onEnter: handleSave })}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="p-1 rounded hover:bg-emerald-500/10 text-emerald-600 disabled:opacity-50"
            title="Speichern"
          >
            <CheckIcon className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="p-1 rounded hover:bg-surface-hover text-content-tertiary disabled:opacity-50"
            title="Abbrechen"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className={`group flex items-center gap-1.5 text-[13px] text-content font-medium text-right max-w-[60%] hover:text-primary-500 ${mono ? 'font-mono' : ''}`}
          title="Bearbeiten"
        >
          <span>{displayValue || '–'}</span>
          <Pencil className="w-3 h-3 text-content-tertiary opacity-0 group-hover:opacity-100 shrink-0" />
        </button>
      )}
    </div>
  );
}

// #1249 — Smart-Button (Odoo-Style Action-Bar Tab-Switcher).
// Counter prominent, Icon klein daneben, Label klein darunter, Border-Card-Style.
function SmartButton({ icon: Icon, label, count, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center justify-center px-3 py-3 rounded-card border transition-all min-h-[80px] ${
        active
          ? 'border-primary-500 bg-primary-500/5 text-primary-600 dark:text-primary-400'
          : 'border-line-card bg-surface-card hover:border-line text-content-secondary hover:text-content'
      }`}
    >
      <div className="flex items-center gap-2 leading-none">
        <Icon className={`w-4 h-4 ${active ? 'text-primary-500' : 'text-content-tertiary'}`} />
        <span className={`text-2xl font-bold tabular-nums ${active ? 'text-primary-600 dark:text-primary-400' : 'text-content'}`}>
          {count ?? 0}
        </span>
      </div>
      <span className="text-[11px] mt-1.5 font-medium">{label}</span>
    </button>
  );
}

// ── Deadline helper ──

function getDeadlineInfo(claim) {
  if (!claim.submission_deadline_days || !claim.created_at) return null;
  if (['resolved', 'rejected'].includes(claim.status)) return null;
  const created = new Date(claim.created_at);
  const deadline = new Date(created.getTime() + claim.submission_deadline_days * 86400000);
  const now = new Date();
  const daysLeft = Math.ceil((deadline - now) / 86400000);
  if (daysLeft < 0) return { type: 'expired', days: Math.abs(daysLeft), date: deadline };
  if (daysLeft <= 3) return { type: 'soon', days: daysLeft, date: deadline };
  return { type: 'ok', days: daysLeft, date: deadline };
}

export default function ClaimDetail() {
  const { t } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();

  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]); // #1216 B1
  const [carriers, setCarriers] = useState([]); // #1226 Bug C
  const [savingField, setSavingField] = useState(false);
  // #1249 — Default-Tab Kommunikation (das ist wo gearbeitet wird). URL ?tab= override gewinnt.
  const [activeTab, setActiveTab] = useTabParam('communication');
  // #1249 — Stammdaten-Akkordeon (Primary 4 Felder default, Rest auf Klick)
  const [showMoreFields, setShowMoreFields] = useState(false);

  // Modals
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: '', description: '' });
  const [resolveForm, setResolveForm] = useState({ resolution_type: 'refund', resolution_recipient: 'weship', resolution_amount: '', resolution_notes: '' });
  const [saving, setSaving] = useState(false);

  // #1236 Klärungsanfrage-Modals
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [showCloseClarificationModal, setShowCloseClarificationModal] = useState(false);
  const [escalateForm, setEscalateForm] = useState({ claim_type: '', carrier_id: '', lager: '', escalation_note: '' });
  const [closeClarificationForm, setCloseClarificationForm] = useState({ outcome: 'delivered', notes: '' });

  // Note
  const [noteText, setNoteText] = useState('');
  const [sendingNote, setSendingNote] = useState(false);

  // Upload
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState('other');

  // Lightbox
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  // #1241 — Original-HTML Modal-State (Mail-Render kompakt + Toggle)
  const [originalModal, setOriginalModal] = useState({ open: false, html: '', subject: '' });

  // Knowledge articles
  const [knowledgeArticles, setKnowledgeArticles] = useState([]);

  // #1246 — Smart-Buttons + bidirektionale Verlinkung
  const [linkedCases, setLinkedCases] = useState([]);
  const [linkedCasesLoading, setLinkedCasesLoading] = useState(false);
  const [linkCaseModalOpen, setLinkCaseModalOpen] = useState(false);
  const [unlinkingCase, setUnlinkingCase] = useState(null);

  // #1219 Block E: Communication (Messages)
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  // #1250 — Multi-Channel: kunde / carrier / dritte / intern (disabled) / all
  const [activeChannel, setActiveChannel] = useState('kunde');
  const [channelSummary, setChannelSummary] = useState({ kunde: { count: 0, unread: 0 }, carrier: { count: 0, unread: 0 }, dritte: { count: 0, unread: 0 }, intern: { count: 0, unread: 0 } });
  const [channelContacts, setChannelContacts] = useState([]);
  const [channelChanging, setChannelChanging] = useState(null); // {msgId, channel}
  const [composer, setComposer] = useState({ subject: '', body: '', in_reply_to: null, message_ids: [], cc_emails: '', bcc_emails: '', show_cc: false, show_bcc: false, channel: 'kunde', to_address: '', to_name: '' });
  const [composerAttachments, setComposerAttachments] = useState([]); // {file_name, mime_type, file_size, blob}
  const [composerDocPicks, setComposerDocPicks] = useState([]); // #1224 P4 — claim_documents.id-Array pre-selected/manuell
  const [composerDocPickerOpen, setComposerDocPickerOpen] = useState(false);
  const [composerSending, setComposerSending] = useState(false);
  const [composerAiLoading, setComposerAiLoading] = useState(false);
  const [composerTemplates, setComposerTemplates] = useState([]);
  const [composerTemplateOpen, setComposerTemplateOpen] = useState(false);
  const [composerRecipientFocus, setComposerRecipientFocus] = useState(false);
  const composerFileRef = useRef(null);
  // #1250 — Forward-Dialog
  const [forwardModal, setForwardModal] = useState({ open: false, msg: null, channel: 'dritte', to_address: '', to_name: '', subject: '', body: '', cc_emails: '', bcc_emails: '', show_cc: false, show_bcc: false });
  // #1161 — Drag&Drop für Composer + Forward
  const [composerDragOver, setComposerDragOver] = useState(false);
  const [forwardDragOver, setForwardDragOver] = useState(false);
  const [forwardAttachments, setForwardAttachments] = useState([]);
  const forwardFileRef = useRef(null);

  const fetchClaim = useCallback(async () => {
    try {
      const data = await api(`/api/claims/${id}`);
      setClaim(data);
    } catch (err) {
      console.error('Claim fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await api('/api/users');
      setUsers(Array.isArray(data) ? data.filter(u => !['portal_kunde', 'portal_mitarbeiter'].includes(u.rolle)) : []);
    } catch { /* ignore */ }
  }, []);

  // #1216 B1: Kunden-Liste für editierbares Kunde-Feld
  const fetchCustomers = useCallback(async () => {
    try {
      const data = await api('/api/kunden');
      setCustomers(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, []);

  // #1226 Bug C: Carrier-Liste für editierbares Carrier-Feld
  const fetchCarriers = useCallback(async () => {
    try {
      const data = await api('/api/claims/carriers');
      setCarriers(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchClaim(); fetchUsers(); fetchCustomers(); fetchCarriers(); }, [fetchClaim, fetchUsers, fetchCustomers, fetchCarriers]);

  // #1246 — Linked Cases laden
  const fetchLinkedCases = useCallback(async () => {
    if (!id) return;
    setLinkedCasesLoading(true);
    try {
      const data = await api(`/api/claims/${id}/linked-cases`);
      setLinkedCases(Array.isArray(data?.rows) ? data.rows : []);
    } catch { setLinkedCases([]); } finally { setLinkedCasesLoading(false); }
  }, [id]);
  useEffect(() => { fetchLinkedCases(); }, [fetchLinkedCases]);

  // #1248 — Per-User View-Tracking (fire-and-forget). Resettet has_active_linked_cases
  // in Claims-Liste + Indigo-Dot in linked Cases im Case-Detail beim nächsten Aufruf.
  useEffect(() => {
    if (!id) return;
    fetch(`${API}/api/claims/${id}/mark-viewed`, { method: 'POST', credentials: 'include' }).catch(() => {});
  }, [id]);

  async function handleUnlinkCase(linkId) {
    if (!confirm(t('links.confirmUnlink', 'Verknüpfung wirklich entfernen?'))) return;
    setUnlinkingCase(linkId);
    try {
      const r = await fetch(`${API}/api/claim-case-links/${linkId}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        alert(err.message || err.error || 'Fehler');
        return;
      }
      await fetchLinkedCases();
      fetchClaim();
    } catch (err) {
      alert(err.message || 'Fehler');
    } finally {
      setUnlinkingCase(null);
    }
  }

  // #1216 B1: Generischer Update-Helfer für editierbare Felder (PUT /api/claims/:id)
  const updateClaim = useCallback(async (patch) => {
    setSavingField(true);
    try {
      await api(`/api/claims/${id}`, { method: 'PUT', body: patch });
      await fetchClaim();
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingField(false);
    }
  }, [id, fetchClaim]);

  // Fetch related knowledge articles when claim loads
  useEffect(() => {
    if (!claim?.carrier_id) return;
    const params = new URLSearchParams();
    if (claim.claim_type) params.set('claim_type', claim.claim_type);
    api(`/api/claims-knowledge/by-carrier/${claim.carrier_id}?${params}`)
      .then(rows => setKnowledgeArticles(rows))
      .catch(() => {});
  }, [claim?.carrier_id, claim?.claim_type]);

  // ── Actions ──

  const handleStatusChange = async () => {
    if (!statusForm.status) return;
    setSaving(true);
    try {
      await api(`/api/claims/${id}/status`, { method: 'PUT', body: statusForm });
      setShowStatusModal(false);
      setStatusForm({ status: '', description: '' });
      fetchClaim();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleResolve = async () => {
    if (!resolveForm.resolution_type) return;
    if (resolveForm.resolution_type === 'refund' && !resolveForm.resolution_recipient) {
      alert(t('claims.detail.resolveModal.recipientRequired') || 'Bitte Empfänger der Gutschrift wählen');
      return;
    }
    setSaving(true);
    try {
      const body = { ...resolveForm };
      if (body.resolution_type !== 'refund') body.resolution_recipient = null;
      await api(`/api/claims/${id}/resolve`, { method: 'PUT', body });
      setShowResolveModal(false);
      fetchClaim();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  // #1236 — Klärung → echter Claim eskalieren
  const handleEscalate = async () => {
    setSaving(true);
    try {
      const body = {};
      if (escalateForm.claim_type) body.claim_type = escalateForm.claim_type;
      if (escalateForm.carrier_id) body.carrier_id = Number(escalateForm.carrier_id);
      if (escalateForm.lager) body.lager = escalateForm.lager;
      if (escalateForm.escalation_note) body.escalation_note = escalateForm.escalation_note;
      await api(`/api/claims/${id}/escalate`, { method: 'POST', body });
      setShowEscalateModal(false);
      setEscalateForm({ claim_type: '', carrier_id: '', lager: '', escalation_note: '' });
      fetchClaim();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  // #1236 — Klärung als erledigt schließen
  const handleCloseClarification = async () => {
    if (!closeClarificationForm.outcome) return;
    setSaving(true);
    try {
      await api(`/api/claims/${id}/close-clarification`, { method: 'POST', body: closeClarificationForm });
      setShowCloseClarificationModal(false);
      fetchClaim();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAssign = async (userId) => {
    try {
      await api(`/api/claims/${id}/assign`, { method: 'PUT', body: { assigned_to: userId || null } });
      fetchClaim();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSendingNote(true);
    try {
      await api(`/api/claims/${id}/notes`, { method: 'POST', body: { description: noteText.trim() } });
      setNoteText('');
      fetchClaim();
    } catch (err) {
      alert(err.message);
    } finally {
      setSendingNote(false);
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('document_type', uploadType);
      for (const f of files) formData.append('files', f);
      await fetch(`${API}/api/claims/${id}/documents`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      }).then(r => r.json());
      fetchClaim();
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!confirm(t('common.confirmDelete') || 'Löschen?')) return;
    try {
      await api(`/api/claims/${id}/documents/${docId}`, { method: 'DELETE' });
      fetchClaim();
    } catch (err) {
      alert(err.message);
    }
  };

  // #1219 Block E: Messages — fetch + composer handlers
  const fetchMessages = useCallback(async () => {
    setLoadingMessages(true);
    try {
      const data = await api(`/api/claims/${id}/messages`);
      setMessages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Messages fetch error:', err);
    } finally {
      setLoadingMessages(false);
    }
  }, [id]);

  // #1250 — Channel-Summary (Counts pro Channel)
  const fetchChannelSummary = useCallback(async () => {
    try {
      const data = await api(`/api/claims/${id}/communication-summary`);
      if (data && typeof data === 'object') setChannelSummary(prev => ({ ...prev, ...data }));
    } catch { /* ignore */ }
  }, [id]);

  useEffect(() => {
    if (activeTab === 'communication' && claim?.id) {
      fetchMessages();
      fetchChannelSummary();
    }
  }, [activeTab, claim?.id, fetchMessages, fetchChannelSummary]);

  // #1250 — Recipient-Autocomplete: lädt bekannte Kontakte für den aktuell gewählten Composer-Channel
  useEffect(() => {
    if (!claim?.id) return;
    if (composer.channel === 'intern' || composer.channel === 'all') { setChannelContacts([]); return; }
    let cancelled = false;
    api(`/api/claims/${id}/contacts?channel=${composer.channel}`)
      .then(rows => { if (!cancelled) setChannelContacts(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setChannelContacts([]); });
    return () => { cancelled = true; };
  }, [id, claim?.id, composer.channel]);

  // #1250 — Sync activeChannel → composer.channel (Default-Empfänger beim Channel-Wechsel)
  useEffect(() => {
    if (activeChannel === 'all') return; // "Alle" ist nur Lesemodus
    if (activeChannel === 'intern') return; // Phase 3
    setComposer(c => c.channel === activeChannel ? c : { ...c, channel: activeChannel, to_address: '', to_name: '' });
  }, [activeChannel]);

  // #1224 P5 — Auto-Markierung aller Inbound-Nachrichten als gelesen (global, beim Öffnen des Claims)
  useEffect(() => {
    if (!claim?.id) return;
    fetch(`${API}/api/claims/${claim.id}/mark-read`, { method: 'POST', credentials: 'include' })
      .catch(() => {});
  }, [claim?.id]);

  // Lade Email-Templates für Composer-Auswahl (claim_carrier_reply_*)
  useEffect(() => {
    if (activeTab !== 'communication' || composerTemplates.length > 0) return;
    api(`/api/email-templates?funktion=claim_carrier_reply_generic`)
      .then(rows1 => api(`/api/email-templates?funktion=claim_carrier_reply_followup`)
        .then(rows2 => setComposerTemplates([
          ...(Array.isArray(rows1) ? rows1 : []),
          ...(Array.isArray(rows2) ? rows2 : []),
        ].filter(r => r.aktiv !== false)))
        .catch(() => setComposerTemplates(Array.isArray(rows1) ? rows1.filter(r => r.aktiv !== false) : [])))
      .catch(() => {});
  }, [activeTab, composerTemplates.length]);

  const stripHtml = (html) => {
    if (!html) return '';
    // <br> / </p>-Breaks zu \n konvertieren, dann Tags entfernen, dann Entities decodieren
    let s = String(html)
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p\s*>/gi, '\n\n')
      .replace(/<\/div\s*>/gi, '\n');
    const tmp = document.createElement('div');
    tmp.innerHTML = s;
    return (tmp.textContent || tmp.innerText || '').replace(/\n{3,}/g, '\n\n');
  };

  const handleComposerAiPreview = async () => {
    setComposerAiLoading(true);
    try {
      const data = await api(`/api/claims/${id}/messages/preview-ai`, {
        method: 'POST',
        body: { context: composer.body || null },
      });
      setComposer(c => ({ ...c, subject: data.subject || c.subject, body: data.body_html || c.body }));
      // #1224 P4 — Empfohlene Anhänge vorauswählen (MVP: alle claim_documents.id aus recommended_attachments)
      if (Array.isArray(data.recommended_attachments) && data.recommended_attachments.length) {
        setComposerDocPicks(prev => {
          const next = new Set(prev);
          for (const d of data.recommended_attachments) {
            const did = typeof d === 'object' ? Number(d.id) : Number(d);
            if (Number.isInteger(did) && did > 0) next.add(did);
          }
          return Array.from(next);
        });
      }
    } catch (err) {
      alert(t('claims.composer.aiError') || `AI-Fehler: ${err.message}`);
    } finally {
      setComposerAiLoading(false);
    }
  };

  const handleComposerTemplatePick = (tpl) => {
    // #1161 — RichTextEditor erwartet HTML; Templates speichern bereits body_html
    setComposer(c => ({
      ...c,
      subject: tpl.betreff || c.subject,
      body: tpl.body_html || c.body,
    }));
    setComposerTemplateOpen(false);
  };

  const handleComposerAttach = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setComposerAttachments(prev => [
      ...prev,
      ...files.map(f => ({ file_name: f.name, mime_type: f.type, file_size: f.size, blob: f })),
    ]);
    if (composerFileRef.current) composerFileRef.current.value = '';
  };

  const handleComposerRemoveAttachment = (idx) => {
    setComposerAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const handleComposerSend = async () => {
    if (!composer.subject.trim() || !composer.body.trim()) {
      alert(t('claims.composer.subjectBodyRequired') || 'Betreff und Inhalt erforderlich');
      return;
    }
    // #1250 — Empfänger-Pflicht: kunde + dritte brauchen to_address (manuell oder Default-Lookup)
    if ((composer.channel === 'dritte') && !composer.to_address.trim()) {
      alert(t('claims.composer.recipientRequired') || 'Empfänger erforderlich');
      return;
    }
    setComposerSending(true);
    try {
      const formData = new FormData();
      formData.append('subject', composer.subject);
      // #1161 — composer.body ist bereits HTML (Tiptap-Output); kein Re-Escape mehr.
      formData.append('body_html', composer.body);
      if (composer.in_reply_to) formData.append('in_reply_to', composer.in_reply_to);
      if (composer.cc_emails && composer.cc_emails.trim()) {
        formData.append('cc_emails', composer.cc_emails.trim());
      }
      if (composer.bcc_emails && composer.bcc_emails.trim()) {
        formData.append('bcc_emails', composer.bcc_emails.trim());
      }
      // #1250 — Channel + manueller Empfänger
      formData.append('channel', composer.channel);
      if (composer.to_address.trim()) formData.append('to_address', composer.to_address.trim());
      if (composer.to_name.trim()) formData.append('to_name', composer.to_name.trim());
      // #1224 P4 — Vorausgewählte Dokumente als document_ids[]
      const selectedDocIds = (composerDocPicks || []).filter(Boolean);
      if (selectedDocIds.length) formData.append('document_ids', JSON.stringify(selectedDocIds));
      for (const att of composerAttachments) formData.append('files', att.blob);
      const r = await fetch(`${API}/api/claims/${id}/messages`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      setComposer({ subject: '', body: '', in_reply_to: null, message_ids: [], cc_emails: '', bcc_emails: '', show_cc: false, show_bcc: false, channel: composer.channel, to_address: '', to_name: '' });
      setComposerAttachments([]);
      setComposerDocPicks([]);
      await fetchMessages();
      fetchChannelSummary();
      fetchClaim();
    } catch (err) {
      alert(err.message);
    } finally {
      setComposerSending(false);
    }
  };

  const handleReplyTo = (msg) => {
    const replySubject = msg.subject?.startsWith('Re: ') ? msg.subject : `Re: ${msg.subject || ''}`;
    const ch = msg.channel || 'carrier';
    // #1250 — Reply behält Channel der Original-Message und befüllt Empfänger
    const toAddr = msg.direction === 'inbound' ? (msg.from_email || '') : (msg.to_address || '');
    const toName = msg.direction === 'inbound' ? (msg.from_name || '') : (msg.to_name || '');
    setActiveChannel(ch);
    setComposer({
      subject: replySubject,
      body: '',
      in_reply_to: msg.message_id || null,
      message_ids: [msg.id],
      cc_emails: '',
      bcc_emails: '',
      show_cc: false,
      show_bcc: false,
      channel: ch,
      to_address: toAddr || '',
      to_name: toName || '',
    });
  };

  // #1250 — Channel einer Nachricht nachträglich korrigieren
  const handleChangeMessageChannel = async (msg, newChannel) => {
    if (!msg?.id || !newChannel || msg.channel === newChannel) return;
    setChannelChanging({ msgId: msg.id, channel: newChannel });
    try {
      await api(`/api/claims/${id}/messages/${msg.id}/channel`, { method: 'PATCH', body: { channel: newChannel } });
      await fetchMessages();
      fetchChannelSummary();
    } catch (err) {
      alert(err.message || (t('claims.messages.channelChangeError') || 'Fehler'));
    } finally {
      setChannelChanging(null);
    }
  };

  // #1250 — Forward an anderen Channel/Empfänger
  const openForwardDialog = (msg) => {
    const fwdSubject = msg.subject?.startsWith('Fwd: ') ? msg.subject : `Fwd: ${msg.subject || ''}`;
    // #1161 — Original-Body als HTML-Quote (RichText-Editor erwartet HTML)
    const origHtml = renderMailBody(msg) || '';
    const dateStr = msg.created_at ? new Date(msg.created_at).toLocaleString('de-AT') : '';
    const headerHtml = `<p>&nbsp;</p><p>--- ${t('claims.forward.originalMessage') || 'Original-Nachricht'} ---</p>` +
      (msg.subject ? `<p><strong>${t('claims.composer.subject') || 'Betreff'}:</strong> ${escapeHtml(msg.subject)}</p>` : '') +
      (msg.from_email ? `<p><strong>${t('claims.forward.from') || 'Von'}:</strong> ${escapeHtml(msg.from_email)}</p>` : '') +
      (dateStr ? `<p><strong>${t('claims.forward.date') || 'Datum'}:</strong> ${escapeHtml(dateStr)}</p>` : '') +
      `<blockquote style="border-left:3px solid #d1d5db;padding-left:12px;margin:8px 0;color:#4b5563">${origHtml}</blockquote>`;
    setForwardModal({ open: true, msg, channel: 'dritte', to_address: '', to_name: '', subject: fwdSubject, body: headerHtml, cc_emails: '', bcc_emails: '', show_cc: false, show_bcc: false });
    setForwardAttachments([]);
  };

  const handleForwardSend = async () => {
    const f = forwardModal;
    if (!f.subject.trim() || !f.body.trim()) {
      alert(t('claims.composer.subjectBodyRequired') || 'Betreff und Inhalt erforderlich');
      return;
    }
    if (!f.to_address.trim()) {
      alert(t('claims.composer.recipientRequired') || 'Empfänger erforderlich');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('subject', f.subject);
      // #1161 — body ist HTML (Tiptap-Output); kein Re-Escape mehr.
      formData.append('body_html', f.body);
      formData.append('channel', f.channel);
      formData.append('to_address', f.to_address.trim());
      if (f.to_name.trim()) formData.append('to_name', f.to_name.trim());
      if (f.cc_emails && f.cc_emails.trim()) formData.append('cc_emails', f.cc_emails.trim());
      if (f.bcc_emails && f.bcc_emails.trim()) formData.append('bcc_emails', f.bcc_emails.trim());
      for (const att of forwardAttachments) formData.append('files', att.blob);
      const r = await fetch(`${API}/api/claims/${id}/messages`, { method: 'POST', credentials: 'include', body: formData });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      setForwardModal({ open: false, msg: null, channel: 'dritte', to_address: '', to_name: '', subject: '', body: '', cc_emails: '', bcc_emails: '', show_cc: false, show_bcc: false });
      setForwardAttachments([]);
      await fetchMessages();
      fetchChannelSummary();
    } catch (err) {
      alert(err.message);
    }
  };

  // ── Loading / Not found ──

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 bg-surface-inset rounded animate-pulse" />
        <div className="grid grid-cols-2 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-48 bg-surface-inset rounded-card animate-pulse" />)}
        </div>
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="text-center py-20">
        <ShieldAlert className="w-12 h-12 mx-auto mb-3 text-content-tertiary" />
        <p className="text-content-secondary">Claim nicht gefunden</p>
        <BackLink to="/claims" label={t('claims.detail.back')} className="mt-4" />
      </div>
    );
  }

  const deadline = getDeadlineInfo(claim);
  // #1216 C1: alle 11 Status außer aktuellem
  const transitions = ALL_STATUSES.filter(s => s !== claim.status);
  const isResolved = ['resolved', 'rejected', 'clarification_resolved'].includes(claim.status);
  const isClarification = claim.status === 'clarification'; // #1236
  const imageDocuments = (claim.documents || []).filter(d => d.mime_type?.startsWith('image/'));
  // #1216 B2 + #1217 A1: dynamisches Label für recipient_info je nach claim_type
  const recipientInfoLabel = claim.claim_type === 'investigation'
    ? t('claims.fields.notesCaseProgress')
    : claim.claim_type === 'damage'
      ? t('claims.recipientInfo')
      : claim.claim_type === 'content_missing'
        ? t('claims.fields.notesContentMissing')
        : t('claims.fields.notes');

  return (
    <div className="p-6">
      {/* #1249 — Header (Title-Zeile + Status-Pills + Actions in 1 Zeile, Subtitle drunter) */}
      <div className="mb-4">
        <BackLink to="/claims" label={t('claims.detail.back')} className="text-[12px] mb-2" />
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-bold tracking-tight text-content">{claim.claim_number}</h1>
              <StatusBadge status={claim.status} t={t} />
              <TypeBadge type={claim.claim_type} t={t} />
              {/* #1236 Klärungs-Ausgang-Badge */}
              {claim.status === 'clarification_resolved' && claim.clarification_outcome && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-[11px] font-medium bg-slate-500/10 text-[var(--text-secondary,#4B5563)] dark:text-[var(--text-tertiary,#9CA3AF)]">
                  {t(`claims.clarificationOutcome.${claim.clarification_outcome}`) || claim.clarification_outcome}
                </span>
              )}
              {deadline?.type === 'expired' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-[11px] font-medium bg-red-500/10 text-red-600 dark:text-red-400">
                  <AlertTriangle className="w-3 h-3" /> {t('claims.detail.deadlineExpired')}
                </span>
              )}
              {deadline?.type === 'soon' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-[11px] font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400">
                  <Clock className="w-3 h-3" /> {t('claims.detail.deadlineSoon')} ({deadline.days}d)
                </span>
              )}
            </div>
            <div className="mt-1.5 text-[12px] text-content-secondary">
              <span className="font-medium text-content">{claim.customer_name || '—'}</span>
              <span className="mx-1.5">·</span>
              <span>{new Date(claim.created_at).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            {/* #1236 — Klärungs-Aktionen ersetzen Status-Change + Resolve im clarification-Status */}
            {isClarification ? (
              <>
                <Button variant="secondary" size="sm" icon={CheckCircle2} onClick={() => setShowCloseClarificationModal(true)}>
                  {t('claims.detail.closeClarification') || 'Klärung erledigt'}
                </Button>
                <Button size="sm" icon={Send} onClick={() => { const rawCarrier = !claim.carrier_id ? matchCarrierByRawName(carriers, claim.carrier_name_raw) : null; setEscalateForm({ claim_type: claim.claim_type || '', carrier_id: claim.carrier_id || rawCarrier?.id || '', lager: claim.lager || '', escalation_note: '' }); setShowEscalateModal(true); }}>
                  {t('claims.detail.escalateToClaim') || 'Zum Schadensfall eskalieren'}
                </Button>
              </>
            ) : (
              <>
                {/* #1216 C1: Status-Wechsel jederzeit verfügbar (auch bei resolved/rejected — z.B. wieder eröffnen) */}
                <Button variant="secondary" size="sm" icon={RefreshCw} onClick={() => { setStatusForm({ status: transitions[0] || '', description: '' }); setShowStatusModal(true); }}>
                  {t('claims.detail.changeStatus')}
                </Button>
                {!isResolved && (
                  <Button size="sm" icon={CheckCircle2} onClick={() => setShowResolveModal(true)}>
                    {t('claims.detail.resolve')}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* #1240 — Reverse-InfoBox: Klärung aus Case konvertiert */}
      {claim.converted_from_case_id && (
        <div className="mb-4 p-3 rounded-card bg-[#338edc]/5 border border-[#338edc]/30 flex items-start gap-2">
          <GitBranch className="w-4 h-4 text-[#338edc] flex-shrink-0 mt-0.5" />
          <div className="text-[12px] leading-relaxed flex-1">
            <span className="font-medium text-[#338edc]">{t('claims.detail.convertedFromTitle') || 'Aus Case konvertiert'}</span>
            <span className="text-[var(--text-secondary,#4B5563)] ml-1">
              {t('claims.detail.convertedFromBody') || 'Diese Klärung wurde aus einem Support-Case erstellt.'}
            </span>
            <button
              type="button"
              onClick={() => navigate(`/cases/${claim.converted_from_case_id}`)}
              className="ml-2 text-[#338edc] hover:underline font-medium"
            >
              {t('claims.detail.openSourceCase') || 'Case anzeigen'} →
            </button>
          </div>
        </div>
      )}

      {/* #1249 — Smart-Button-Row (Odoo-Style, prominenter Tab-Switcher direkt unter Header) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <SmartButton
          icon={Clock}
          label={t('claims.detail.tabs.timeline')}
          count={claim.counts?.timeline ?? (claim.timeline?.length || 0)}
          active={activeTab === 'timeline'}
          onClick={() => setActiveTab('timeline')}
        />
        <SmartButton
          icon={MessageSquare}
          label={t('claims.detail.tabs.communication')}
          count={claim.counts?.messages ?? messages.length}
          active={activeTab === 'communication'}
          onClick={() => setActiveTab('communication')}
        />
        <SmartButton
          icon={FileText}
          label={t('claims.detail.tabs.documents')}
          count={claim.counts?.documents ?? (claim.documents?.length || 0)}
          active={activeTab === 'documents'}
          onClick={() => setActiveTab('documents')}
        />
        <SmartButton
          icon={GitBranch}
          label={t('claims.detail.tabs.linkedCases', 'Cases')}
          count={claim.counts?.linked_cases ?? linkedCases.length}
          active={activeTab === 'linked-cases'}
          onClick={() => setActiveTab('linked-cases')}
        />
      </div>

      {/* #1249 — Stammdaten (60%) + Status (40%) kompakt nebeneinander */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-4">

        {/* Stammdaten — 3/5 (60%) */}
        <div className="lg:col-span-3 bg-surface-card border border-line-card rounded-card p-5">
          <h3 className="text-[13px] font-bold text-content mb-3">{t('claims.detail.basicData')}</h3>
          {/* #1226 Bug D: Warnung wenn keine Benachrichtigungs-E-Mail für den Kunden hinterlegt */}
          {claim.customer_id && !claim.has_notification_email && (
            <div className="mb-3 px-3 py-2 rounded-button bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400 text-[12px] flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>{t('claims.detail.noNotificationEmail') || 'Keine Benachrichtigungs-E-Mail hinterlegt — Status-Updates erreichen den Kunden nicht. Stammdaten → Kunden-E-Mail-Adressen ergänzen.'}</span>
            </div>
          )}

          {/* Primary 4 Felder — immer sichtbar (Kunde, Bestellnummer, Sendungsnummer, Datum) */}
          <EditableInfoRow
            label={t('claims.dashboard.filterCustomer')}
            value={claim.customer_id ?? ''}
            displayValue={claim.customer_name}
            saving={savingField}
            onSave={(v) => updateClaim({ customer_id: v ? Number(v) : null })}
            editor={({ value, setValue }) => (
              <select
                value={value || ''}
                onChange={e => setValue(e.target.value)}
                className="px-2 py-1 text-[12px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500 max-w-[200px]"
                autoFocus
              >
                <option value="">{t('claims.fields.noCustomer')}</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
          />
          <EditableInfoRow
            label={t('claims.orderNumber')}
            value={claim.order_number ?? ''}
            displayValue={claim.order_number}
            saving={savingField}
            onSave={(v) => updateClaim({ order_number: v?.trim() || null })}
            mono
            editor={({ value, setValue, onEnter }) => (
              <input
                type="text"
                value={value}
                onChange={e => setValue(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && onEnter()}
                className="px-2 py-1 text-[12px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500 w-40 text-right font-mono"
                autoFocus
              />
            )}
          />
          <EditableInfoRow
            label={t('claims.trackingNumber')}
            value={claim.tracking_number ?? ''}
            displayValue={claim.tracking_number}
            saving={savingField}
            onSave={(v) => updateClaim({ tracking_number: v?.trim() || null })}
            mono
            editor={({ value, setValue, onEnter }) => (
              <input
                type="text"
                value={value}
                onChange={e => setValue(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && onEnter()}
                className="px-2 py-1 text-[12px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500 w-40 text-right font-mono"
                autoFocus
              />
            )}
          />
          <InfoRow label={t('common.date') || 'Erstellt'} value={new Date(claim.created_at).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} />

          {/* #1249 — Akkordeon: weitere Details auf Klick */}
          <button
            type="button"
            onClick={() => setShowMoreFields(s => !s)}
            className="mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
          >
            <span aria-hidden>{showMoreFields ? '▴' : '▾'}</span>
            <span>{showMoreFields
              ? (t('claims.detail.lessDetails') || 'Weniger anzeigen')
              : (t('claims.detail.moreDetails') || 'Weitere Details anzeigen')}</span>
          </button>

          {showMoreFields && (
            <div className="mt-2 pt-2 border-t border-line">
              <EditableInfoRow
                label={t('claims.carrier')}
                value={claim.carrier_id ?? ''}
                displayValue={claim.carrier_name || claim.carrier_name_raw}
                saving={savingField}
                onSave={(v) => updateClaim({ carrier_id: v ? Number(v) : null })}
                editor={({ value, setValue }) => (
                  <select
                    value={value || ''}
                    onChange={e => setValue(e.target.value)}
                    className="px-2 py-1 text-[12px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500 max-w-[200px]"
                    autoFocus
                  >
                    <option value="">—</option>
                    {carriers.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                )}
              />
              <EditableInfoRow
                label={t('claims.fields.lager')}
                value={claim.lager ?? ''}
                displayValue={claim.lager === 'AT' ? t('claims.form.lagerAT') : claim.lager === 'DE' ? t('claims.form.lagerDE') : null}
                saving={savingField}
                onSave={(v) => updateClaim({ lager: v || null })}
                editor={({ value, setValue }) => (
                  <select
                    value={value || ''}
                    onChange={e => setValue(e.target.value)}
                    className="px-2 py-1 text-[12px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                    autoFocus
                  >
                    <option value="">—</option>
                    <option value="AT">{t('claims.form.lagerAT')}</option>
                    <option value="DE">{t('claims.form.lagerDE')}</option>
                  </select>
                )}
              />
              <EditableInfoRow
                label={t('claims.goodsValue')}
                value={claim.goods_value ?? ''}
                displayValue={claim.goods_value != null ? `€${Number(claim.goods_value).toLocaleString('de-AT')}` : null}
                saving={savingField}
                onSave={(v) => {
                  const trimmed = String(v).trim().replace(',', '.');
                  const num = trimmed === '' ? null : parseFloat(trimmed);
                  if (trimmed !== '' && (Number.isNaN(num) || num < 0)) {
                    alert(t('claims.fields.invalidNumber'));
                    return Promise.reject(new Error('invalid'));
                  }
                  return updateClaim({ goods_value: num });
                }}
                editor={({ value, setValue, onEnter }) => (
                  <input
                    type="text"
                    inputMode="decimal"
                    value={value}
                    onChange={e => setValue(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && onEnter()}
                    placeholder="z.B. 245,90"
                    className="px-2 py-1 text-[12px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500 w-32 text-right"
                    autoFocus
                  />
                )}
              />
              <EditableInfoRow
                label={recipientInfoLabel}
                value={claim.recipient_info ?? ''}
                displayValue={claim.recipient_info}
                saving={savingField}
                onSave={(v) => updateClaim({ recipient_info: v?.trim() || null })}
                editor={({ value, setValue, onEnter }) => (
                  <input
                    type="text"
                    value={value}
                    onChange={e => setValue(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && onEnter()}
                    className="px-2 py-1 text-[12px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500 w-44 text-right"
                    autoFocus
                  />
                )}
              />
              <EditableInfoRow
                label={t('claims.carrierReference')}
                value={claim.carrier_reference ?? ''}
                displayValue={claim.carrier_reference}
                saving={savingField}
                onSave={(v) => updateClaim({ carrier_reference: v?.trim() || null })}
                mono
                editor={({ value, setValue, onEnter }) => (
                  <input
                    type="text"
                    value={value}
                    onChange={e => setValue(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && onEnter()}
                    className="px-2 py-1 text-[12px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500 w-40 text-right font-mono"
                    autoFocus
                  />
                )}
              />
              <EditableInfoRow
                label={t('claims.fields.submissionChannel')}
                value={claim.submission_channel ?? ''}
                displayValue={claim.submission_channel ? t(`claims.form.submissionChannel${
                  claim.submission_channel === 'business_cockpit' ? 'BusinessCockpit'
                  : claim.submission_channel.charAt(0).toUpperCase() + claim.submission_channel.slice(1)
                }`) : null}
                saving={savingField}
                onSave={(v) => updateClaim({ submission_channel: v || null })}
                editor={({ value, setValue }) => (
                  <select
                    value={value || ''}
                    onChange={e => setValue(e.target.value)}
                    className="px-2 py-1 text-[12px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                    autoFocus
                  >
                    <option value="">—</option>
                    <option value="business_cockpit">{t('claims.form.submissionChannelBusinessCockpit')}</option>
                    <option value="email">{t('claims.form.submissionChannelEmail')}</option>
                    <option value="phone">{t('claims.form.submissionChannelPhone')}</option>
                    <option value="portal">{t('claims.form.submissionChannelPortal')}</option>
                    <option value="manual">{t('claims.form.submissionChannelManual')}</option>
                  </select>
                )}
              />
              {(claim.post_tracking_nr || (claim.carrier_name || '').toLowerCase().includes('post')) && (
                <EditableInfoRow
                  label={t('claims.fields.postTrackingNr')}
                  value={claim.post_tracking_nr ?? ''}
                  displayValue={claim.post_tracking_nr}
                  saving={savingField}
                  onSave={(v) => updateClaim({ post_tracking_nr: v?.trim() || null })}
                  mono
                  editor={({ value, setValue, onEnter }) => (
                    <input
                      type="text"
                      value={value}
                      onChange={e => setValue(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && onEnter()}
                      className="px-2 py-1 text-[12px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500 w-40 text-right font-mono"
                      autoFocus
                    />
                  )}
                />
              )}
              <EditableInfoRow
                label={t('claims.fields.carrierTicketNr')}
                value={claim.carrier_ticket_nr ?? ''}
                displayValue={claim.carrier_ticket_nr}
                saving={savingField}
                onSave={(v) => updateClaim({ carrier_ticket_nr: v?.trim() || null })}
                mono
                editor={({ value, setValue, onEnter }) => (
                  <input
                    type="text"
                    value={value}
                    onChange={e => setValue(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && onEnter()}
                    className="px-2 py-1 text-[12px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500 w-40 text-right font-mono"
                    autoFocus
                  />
                )}
              />
              <EditableInfoRow
                label={t('claims.fields.returnReference')}
                value={claim.return_reference ?? ''}
                displayValue={claim.return_reference}
                saving={savingField}
                onSave={(v) => updateClaim({ return_reference: v?.trim() || null })}
                mono
                editor={({ value, setValue, onEnter }) => (
                  <input
                    type="text"
                    value={value}
                    onChange={e => setValue(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && onEnter()}
                    className="px-2 py-1 text-[12px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500 w-40 text-right font-mono"
                    autoFocus
                  />
                )}
              />
              <EditableInfoRow
                label={t('claims.fields.caseProgressNote')}
                value={claim.case_progress_note ?? ''}
                displayValue={claim.case_progress_note}
                saving={savingField}
                onSave={(v) => updateClaim({ case_progress_note: v?.trim() || null })}
                editor={({ value, setValue }) => (
                  <textarea
                    value={value}
                    onChange={e => setValue(e.target.value)}
                    rows={3}
                    className="px-2 py-1 text-[12px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500 w-full"
                    autoFocus
                  />
                )}
              />
              {claim.description && (
                <div className="mt-3 pt-3 border-t border-line">
                  <div className="text-[12px] font-semibold text-content-secondary mb-1">{t('claims.detail.details')}</div>
                  <p className="text-[12.5px] text-content-secondary leading-relaxed whitespace-pre-wrap">{claim.description}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Status-Block — 2/5 (40%) */}
        <div className="lg:col-span-2 space-y-4">
          {/* Status Card */}
          <div className="bg-surface-card border border-line-card rounded-card p-5">
            <h3 className="text-[13px] font-bold text-content mb-3">{t('claims.detail.currentStatus')}</h3>
            <div className="space-y-3">
              <div><StatusBadge status={claim.status} t={t} /></div>
              <div>
                <label className="text-[11px] text-content-tertiary font-medium block mb-1">{t('claims.detail.assignTo')}</label>
                <select
                  value={claim.assigned_to || ''}
                  onChange={e => handleAssign(e.target.value ? Number(e.target.value) : null)}
                  className="w-full px-2 py-1.5 text-[12px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">{t('claims.detail.assignTo')}...</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.vorname} {u.nachname}</option>
                  ))}
                </select>
              </div>
              {deadline && (
                <div>
                  <label className="text-[11px] text-content-tertiary font-medium block mb-1">{t('claims.detail.deadline')}</label>
                  <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-badge text-[12px] font-medium ${
                    deadline.type === 'expired'
                      ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                      : deadline.type === 'soon'
                        ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                        : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                  }`}>
                    <Clock className="w-3 h-3" />
                    {deadline.date.toLocaleDateString('de-AT')}
                    <span className="opacity-80">· {deadline.days} {t('claims.dashboard.days')} {deadline.type === 'expired' ? 'überschritten' : 'verbleibend'}</span>
                  </div>
                </div>
              )}
            </div>
            {claim.playbook_damage && (claim.claim_type === 'damage' || claim.claim_type === 'content_missing') && (
              <div className="mt-3 p-3 bg-surface-inset rounded-lg text-[12px] text-content-secondary">
                <span className="font-semibold text-content">{t('claims.playbook')}:</span>
                <p className="mt-1 whitespace-pre-wrap">{claim.playbook_damage}</p>
              </div>
            )}
            {claim.playbook_investigation && claim.claim_type === 'investigation' && (
              <div className="mt-3 p-3 bg-surface-inset rounded-lg text-[12px] text-content-secondary">
                <span className="font-semibold text-content">{t('claims.playbook')}:</span>
                <p className="mt-1 whitespace-pre-wrap">{claim.playbook_investigation}</p>
              </div>
            )}
          </div>

          {/* Result Card (only if resolved) */}
          {isResolved && claim.resolution_type && (
            <div className="bg-surface-card border border-line-card rounded-card p-5">
              <h3 className="text-[13px] font-bold text-content mb-3">{t('claims.detail.result')}</h3>
              <InfoRow label={t('claims.detail.resolveModal.type')} value={t(`claims.resolution.${claim.resolution_type}`) || claim.resolution_type} />
              {claim.resolution_type === 'refund' && claim.resolution_recipient && (
                <InfoRow
                  label={t('claims.detail.resolveModal.recipient')}
                  value={t(`claims.detail.resolveModal.recipient_${claim.resolution_recipient}`)}
                />
              )}
              {claim.resolution_amount != null && (
                <InfoRow label={t('claims.detail.refundAmount')} value={`€${Number(claim.resolution_amount).toLocaleString('de-AT')}`} />
              )}
              {claim.resolution_notes && (
                <div className="mt-2 text-[12px] text-content-secondary whitespace-pre-wrap">{claim.resolution_notes}</div>
              )}
              {claim.resolved_at && (
                <InfoRow label={t('common.date') || 'Abgeschlossen'} value={new Date(claim.resolved_at).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' })} />
              )}
            </div>
          )}

          {/* Related Knowledge Articles */}
          {knowledgeArticles.length > 0 && (
            <div className="bg-surface-card border border-line-card rounded-card p-5">
              <h3 className="text-[13px] font-bold text-content mb-3">{t('claims.knowledge.relatedArticles')}</h3>
              <div className="space-y-1.5">
                {knowledgeArticles.map(art => (
                  <a
                    key={art.id}
                    href={`/claims/knowledge`}
                    onClick={e => { e.preventDefault(); window.open(`/claims/knowledge?article=${art.id}`, '_blank'); }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] text-content-secondary hover:bg-surface-hover hover:text-[#338edc] transition-colors group"
                  >
                    <BookOpen size={13} className="shrink-0 text-content-tertiary group-hover:text-[#338edc]" />
                    <span className="truncate">{art.title}</span>
                    {art.carrier_name && <span className="ml-auto text-[11px] text-content-tertiary shrink-0">{art.carrier_name}</span>}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-surface-card border border-line-card rounded-card p-6">

        {/* ═══ Timeline Tab ═══ */}
        {activeTab === 'timeline' && (
          <div className="relative">
            {(claim.timeline || []).length === 0 ? (
              <p className="text-[13px] text-content-secondary py-4">{t('claims.noClaims')}</p>
            ) : (
              <div className="space-y-0">
                {(claim.timeline || []).map((event, i) => {
                  const iconCfg = TIMELINE_ICONS[event.event_type] || TIMELINE_ICONS.note;
                  const { Icon, color } = iconCfg;
                  const isLast = i === claim.timeline.length - 1;
                  return (
                    <div key={event.id} className="flex gap-3 pb-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-7 h-7 rounded-full bg-surface-inset flex items-center justify-center shrink-0 ${color}`}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        {!isLast && <div className="w-px flex-1 bg-line mt-1" />}
                      </div>
                      <div className="flex-1 min-w-0 pt-0.5">
                        <p className="text-[13px] text-content">{event.description}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[11px] text-content-tertiary">
                            {new Date(event.created_at).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {event.created_by_name && (
                            <span className="text-[11px] text-content-tertiary">• {event.created_by_name}</span>
                          )}
                          {event.is_system && (
                            <span className="text-[10px] text-content-tertiary bg-surface-inset px-1.5 py-0.5 rounded">System</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ═══ Documents Tab ═══ */}
        {activeTab === 'documents' && (
          <div>
            {/* Upload bar */}
            <div className="flex items-center gap-3 mb-4">
              <select
                value={uploadType}
                onChange={e => setUploadType(e.target.value)}
                className="px-2 py-1.5 text-[12px] bg-surface-card border border-line rounded-button text-content"
              >
                {['damage_photo', 'packaging_photo', 'goods_value_proof', 'sales_invoice', 'carrier_correspondence', 'other'].map(dt => (
                  <option key={dt} value={dt}>{t(`claims.documents.${dt}`)}</option>
                ))}
              </select>
              <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip" className="hidden" onChange={handleUpload} />
              <Button variant="secondary" size="sm" icon={Upload} loading={uploading} onClick={() => fileInputRef.current?.click()}>
                {t('claims.detail.uploadDocument')}
              </Button>
            </div>

            {/* Documents grid */}
            {(claim.documents || []).length === 0 ? (
              <p className="text-[13px] text-content-secondary py-4">{t('claims.noDocuments')}</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {(claim.documents || []).map((doc, idx) => {
                  const isImage = doc.mime_type?.startsWith('image/');
                  const DocIcon = DOC_TYPE_ICONS[doc.document_type] || FileText;
                  const docKey = doc.uid || doc.id;
                  const imageIdx = isImage ? imageDocuments.findIndex(d => (d.uid || d.id) === docKey) : -1;
                  return (
                    <div key={docKey} className="border border-line rounded-lg overflow-hidden group">
                      {/* Thumbnail / Icon */}
                      <div
                        className={`h-28 flex items-center justify-center bg-surface-inset ${isImage ? 'cursor-pointer' : ''}`}
                        onClick={() => isImage && setLightboxIdx(imageIdx)}
                      >
                        {isImage ? (
                          <img
                            src={docDownloadUrl(doc)}
                            alt={doc.file_name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <DocIcon className="w-8 h-8 text-content-tertiary" />
                        )}
                      </div>
                      {/* Info */}
                      <div className="p-2">
                        <p className="text-[11px] text-content truncate" title={doc.file_name}>{doc.file_name}</p>
                        <p className="text-[10px] text-content-tertiary mt-0.5">
                          {t(`claims.documents.${doc.document_type}`)}
                          {doc.file_size && ` • ${(doc.file_size / 1024).toFixed(0)} KB`}
                        </p>
                        <div className="flex items-center gap-1 mt-1.5">
                          <a
                            href={docDownloadUrl(doc)}
                            className="p-1 rounded hover:bg-surface-hover text-content-tertiary hover:text-content"
                            title="Download"
                            onClick={e => e.stopPropagation()}
                          >
                            <Download className="w-3.5 h-3.5" />
                          </a>
                          {doc.source !== 'message' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteDoc(doc.id); }}
                              className="p-1 rounded hover:bg-red-500/10 text-content-tertiary hover:text-red-500"
                              title="Löschen"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ═══ Communication Tab — #1219 Block E + #1241 kompakt-Rendering + #1250 Multi-Channel ═══ */}
        {activeTab === 'communication' && (
          <div>
            {/* #1250 — Channel-Tabs */}
            <div className="flex items-center gap-1 mb-3 p-[3px] rounded-[10px] bg-[var(--bg-inset,#F8F9FC)] border border-line w-fit">
              {[
                { key: 'kunde',   icon: '👤', label: t('claims.channels.kunde')   || 'Kunde',   activeClass: 'bg-surface-card shadow-sm text-indigo-600' },
                { key: 'carrier', icon: '🚚', label: t('claims.channels.carrier') || 'Carrier', activeClass: 'bg-surface-card shadow-sm text-amber-600' },
                { key: 'dritte',  icon: '🏢', label: t('claims.channels.dritte')  || 'Dritte',  activeClass: 'bg-surface-card shadow-sm text-slate-700 dark:text-slate-300' },
                { key: 'all',     icon: '📜', label: t('claims.channels.all')     || 'Alle',    activeClass: 'bg-surface-card shadow-sm text-content' },
              ].map(tab => {
                const sum = channelSummary[tab.key] || { count: 0, unread: 0 };
                const total = tab.key === 'all'
                  ? (channelSummary.kunde?.count || 0) + (channelSummary.carrier?.count || 0) + (channelSummary.dritte?.count || 0)
                  : (sum.count || 0);
                const unread = tab.key === 'all' ? 0 : (sum.unread || 0);
                const isActive = activeChannel === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveChannel(tab.key)}
                    className={`px-3 py-1.5 rounded-[8px] text-[12px] font-medium flex items-center gap-1.5 transition-all ${
                      isActive ? tab.activeClass : 'text-content-secondary hover:text-content'
                    }`}
                  >
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                    {total > 0 && (
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                        isActive ? 'bg-surface-inset text-content' : 'bg-surface-card text-content-tertiary'
                      }`}>
                        {total}
                      </span>
                    )}
                    {unread > 0 && (
                      <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-primary-500 text-white">
                        {unread}
                      </span>
                    )}
                  </button>
                );
              })}
              {/* #1251 Phase 3 — Intern (Universal-Chatter) */}
              <button
                type="button"
                onClick={() => setActiveChannel('intern')}
                className={`px-3 py-1.5 rounded-[8px] text-[12px] font-medium flex items-center gap-1.5 transition-all ${
                  activeChannel === 'intern'
                    ? 'bg-surface-card shadow-sm text-emerald-600'
                    : 'text-content-secondary hover:text-content'
                }`}
                title={t('claims.channels.internHint') || 'Interne Notizen + @-Mentions'}
              >
                <span>🔒</span>
                <span>{t('claims.channels.intern') || 'Intern'}</span>
              </button>
            </div>

            {/* #1251 Phase 3 — Intern Channel: Universal-Chatter */}
            {activeChannel === 'intern' && (
              <UniversalChatter entityType="claim" entityId={parseInt(id, 10)} />
            )}

            {/* Full-width Cards: Indigo-Left-Border bei outbound, Emerald-Left-Border bei inbound */}
            <div className={`space-y-3 mb-4 ${activeChannel === 'intern' ? 'hidden' : ''}`}>
              {loadingMessages && (
                <div className="text-[13px] text-content-secondary py-4 text-center">{t('common.loading') || 'Lade…'}</div>
              )}
              {!loadingMessages && messages.filter(m => activeChannel === 'all' || (m.channel || 'carrier') === activeChannel).length === 0 && (
                <p className="text-[13px] text-content-secondary py-4 text-center">{t('claims.messages.empty') || 'Keine Nachrichten vorhanden'}</p>
              )}
              {messages.filter(m => activeChannel === 'all' || (m.channel || 'carrier') === activeChannel).map(msg => {
                const isInbound = msg.direction === 'inbound';
                const cleanedHtml = renderMailBody(msg);
                const showOriginalToggle = hasOriginalHtml(msg);
                const msgChannel = msg.channel || 'carrier';
                const channelColor = msgChannel === 'kunde'
                  ? 'border-l-indigo-500'
                  : msgChannel === 'dritte'
                    ? 'border-l-slate-500'
                    : 'border-l-amber-500'; // carrier (default)
                const channelBadge = msgChannel === 'kunde'
                  ? { icon: '👤', label: t('claims.channels.kunde') || 'Kunde', cls: 'bg-indigo-500/10 text-indigo-600' }
                  : msgChannel === 'dritte'
                    ? { icon: '🏢', label: t('claims.channels.dritte') || 'Dritte', cls: 'bg-slate-500/10 text-slate-600 dark:text-slate-300' }
                    : { icon: '🚚', label: t('claims.channels.carrier') || 'Carrier', cls: 'bg-amber-500/10 text-amber-600' };
                return (
                  <div
                    key={msg.id}
                    className={`rounded-lg p-4 border bg-surface-card border-line-card border-l-[3px] ${channelColor}`}
                  >
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {isInbound ? (
                        <FileText className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                      ) : (
                        <Send className="w-3.5 h-3.5 text-indigo-500" />
                      )}
                      <span className="text-[11px] font-medium text-content">
                        {isInbound ? (msg.from_email || msg.from_name || t('claims.messages.partner') || 'Partner') : (msg.sent_by_name || 'WeShip')}
                      </span>
                      {!isInbound && msg.to_address && (
                        <span className="text-[10px] text-content-tertiary">→ {msg.to_address}</span>
                      )}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${channelBadge.cls}`}>
                        {channelBadge.icon} {channelBadge.label}
                      </span>
                      <span className="text-[10px] text-content-tertiary">
                        {new Date(msg.created_at).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {showOriginalToggle && (
                        <button
                          type="button"
                          onClick={() => setOriginalModal({ open: true, html: getOriginalHtml(msg), subject: msg.subject || '' })}
                          className="ml-auto text-[10px] px-2 py-0.5 rounded border border-line text-content-tertiary hover:text-content hover:bg-surface-hover"
                          title={t('claims.messages.viewOriginal') || 'Original-E-Mail anzeigen'}
                        >
                          📧 {t('claims.messages.original') || 'Original anzeigen'}
                        </button>
                      )}
                    </div>
                    {msg.subject && (
                      <div className="text-[12.5px] font-semibold text-content mb-1.5">{msg.subject}</div>
                    )}
                    <div
                      className="text-[13px] text-content max-w-none [&_p]:my-1.5 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_a]:text-primary-500 [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1.5 [&_li]:my-0.5 [&_strong]:font-semibold [&_em]:italic [&_blockquote]:border-l-2 [&_blockquote]:border-line [&_blockquote]:pl-3 [&_blockquote]:my-2 [&_blockquote]:text-content-tertiary"
                      dangerouslySetInnerHTML={{ __html: cleanedHtml }}
                    />
                    {(msg.attachments?.length > 0) && (
                      <div className="flex flex-wrap gap-1.5 mt-3 pt-2.5 border-t border-line/60">
                        {msg.attachments.map(att => (
                          <a
                            key={att.id}
                            href={`${API}/api/claims/messages/attachments/${att.id}/download`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] bg-surface-inset border border-line hover:bg-surface-hover text-content"
                            title={att.file_name}
                          >
                            <Download className="w-3 h-3" />
                            <span className="truncate max-w-[180px]">{att.file_name}</span>
                          </a>
                        ))}
                      </div>
                    )}
                    {/* #1250 — Quick-Actions: Reply, Forward, Channel ändern */}
                    {!isResolved && (
                      <div className="mt-2.5 pt-2.5 border-t border-line/60 flex items-center gap-3 flex-wrap">
                        {isInbound && (
                          <button
                            onClick={() => handleReplyTo(msg)}
                            className="text-[11px] text-primary-500 hover:text-primary-600 font-medium"
                          >
                            ↩ {t('claims.messages.reply') || 'Antworten'}
                          </button>
                        )}
                        <button
                          onClick={() => openForwardDialog(msg)}
                          className="text-[11px] text-content-secondary hover:text-content"
                        >
                          ⇨ {t('claims.messages.forward') || 'Weiterleiten'}
                        </button>
                        <div className="flex items-center gap-1 text-[11px] text-content-tertiary">
                          <span>{t('claims.messages.channelLabel') || 'Channel'}:</span>
                          <select
                            value={msgChannel}
                            disabled={channelChanging?.msgId === msg.id}
                            onChange={(e) => handleChangeMessageChannel(msg, e.target.value)}
                            className="px-1.5 py-0.5 text-[11px] bg-surface-card border border-line rounded text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                          >
                            <option value="kunde">👤 {t('claims.channels.kunde') || 'Kunde'}</option>
                            <option value="carrier">🚚 {t('claims.channels.carrier') || 'Carrier'}</option>
                            <option value="dritte">🏢 {t('claims.channels.dritte') || 'Dritte'}</option>
                          </select>
                        </div>
                        <button
                          disabled
                          title={t('claims.channels.internSoon') || 'Phase 3 — bald verfügbar'}
                          className="text-[11px] text-content-tertiary opacity-50 cursor-not-allowed"
                        >
                          🔒 {t('claims.messages.referenceIntern') || 'Intern referenzieren'}
                        </button>
                        <div className="ml-auto" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Composer */}
            {!isResolved && activeChannel !== 'all' && activeChannel !== 'intern' && (
              <div className="pt-4 border-t border-line">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[13px] font-semibold text-content">
                    {composer.channel === 'kunde'  && (t('claims.composer.titleKunde')   || 'Antwort an Kunde')}
                    {composer.channel === 'carrier' && (t('claims.composer.titleCarrier') || 'Antwort an Carrier')}
                    {composer.channel === 'dritte' && (t('claims.composer.titleDritte')  || 'Nachricht an Dritte')}
                  </h4>
                  {composer.in_reply_to && (
                    <span className="text-[10px] text-content-tertiary">↩ {t('claims.composer.replyToLast') || 'Antwort auf letzte Nachricht'}</span>
                  )}
                </div>
                <div className="space-y-2">
                  {/* #1250 — Channel-Select */}
                  <div className="flex items-center gap-2 text-[12px]">
                    <span className="w-12 text-content-tertiary">{t('claims.composer.channelLabel') || 'Channel:'}</span>
                    <select
                      value={composer.channel}
                      onChange={e => setComposer(c => ({ ...c, channel: e.target.value, to_address: '', to_name: '' }))}
                      className="px-2 py-1 text-[12px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                    >
                      <option value="kunde">👤 {t('claims.channels.kunde')   || 'Kunde'}</option>
                      <option value="carrier">🚚 {t('claims.channels.carrier') || 'Carrier'}</option>
                      <option value="dritte">🏢 {t('claims.channels.dritte')  || 'Dritte'}</option>
                    </select>
                  </div>
                  {/* #1250 — Empfänger: read-only Default für carrier, editierbar für kunde+dritte */}
                  <div className="flex flex-col gap-1.5 text-[12px]">
                    <div className="flex items-center gap-2 relative">
                      <span className="w-12 text-content-tertiary">{t('claims.composer.to') || 'An:'}</span>
                      {composer.channel === 'carrier' ? (
                        claim.submission_email ? (
                          <span className="text-content font-mono">{composer.to_address.trim() || claim.submission_email}</span>
                        ) : (
                          <span className="text-red-600">{t('claims.composer.noSubmissionEmail') || 'Carrier hat keine E-Mail-Adresse'}</span>
                        )
                      ) : (
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            value={composer.to_address}
                            onChange={e => setComposer(c => ({ ...c, to_address: e.target.value }))}
                            onFocus={() => setComposerRecipientFocus(true)}
                            onBlur={() => setTimeout(() => setComposerRecipientFocus(false), 200)}
                            placeholder={t('claims.composer.recipientPlaceholder') || 'empfaenger@example.com'}
                            className="w-full px-2 py-1 text-[12px] bg-surface-card border border-line rounded-button text-content placeholder:text-content-tertiary focus:outline-none focus:ring-1 focus:ring-primary-500 font-mono"
                          />
                          {composerRecipientFocus && channelContacts.length > 0 && (
                            <div className="absolute z-20 mt-1 left-0 right-0 bg-surface-card border border-line-card rounded-card shadow-lg max-h-48 overflow-y-auto">
                              {channelContacts
                                .filter(ct => !composer.to_address.trim() || ct.email?.toLowerCase().includes(composer.to_address.toLowerCase()) || ct.name?.toLowerCase().includes(composer.to_address.toLowerCase()))
                                .slice(0, 10)
                                .map((ct, idx) => (
                                  <button
                                    key={`${ct.email}-${idx}`}
                                    type="button"
                                    onMouseDown={e => e.preventDefault()}
                                    onClick={() => setComposer(c => ({ ...c, to_address: ct.email || '', to_name: ct.name || '' }))}
                                    className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-surface-hover text-content border-b border-line last:border-0"
                                  >
                                    <div className="font-mono">{ct.email}</div>
                                    {ct.name && <div className="text-[10px] text-content-tertiary">{ct.name}</div>}
                                  </button>
                                ))}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="ml-auto flex items-center gap-2">
                        {!composer.show_cc && (
                          <button
                            type="button"
                            onClick={() => setComposer(c => ({ ...c, show_cc: true }))}
                            className="text-[11px] text-primary-600 hover:underline whitespace-nowrap"
                          >
                            + {t('claims.composer.addCc') || 'CC hinzufügen'}
                          </button>
                        )}
                        {!composer.show_bcc && (
                          <button
                            type="button"
                            onClick={() => setComposer(c => ({ ...c, show_bcc: true }))}
                            className="text-[11px] text-primary-600 hover:underline whitespace-nowrap"
                          >
                            + {t('claims.composer.addBcc') || 'BCC hinzufügen'}
                          </button>
                        )}
                      </div>
                    </div>
                    {composer.show_cc && (
                      <div className="flex items-center gap-2">
                        <span className="w-12 text-content-tertiary">{t('claims.composer.cc') || 'CC:'}</span>
                        <input
                          type="text"
                          value={composer.cc_emails}
                          onChange={e => setComposer(c => ({ ...c, cc_emails: e.target.value }))}
                          placeholder={t('claims.composer.ccPlaceholder') || 'name@example.com, weitere@example.com'}
                          className="flex-1 px-2 py-1.5 text-[12px] bg-surface-card border border-line rounded-button text-content placeholder:text-content-tertiary focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                        <button
                          type="button"
                          onClick={() => setComposer(c => ({ ...c, cc_emails: '', show_cc: false }))}
                          className="text-content-tertiary hover:text-red-500"
                          aria-label={t('common.remove') || 'Entfernen'}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    {composer.show_bcc && (
                      <div className="flex items-center gap-2">
                        <span className="w-12 text-content-tertiary">{t('claims.composer.bcc') || 'BCC:'}</span>
                        <input
                          type="text"
                          value={composer.bcc_emails}
                          onChange={e => setComposer(c => ({ ...c, bcc_emails: e.target.value }))}
                          placeholder={t('claims.composer.bccPlaceholder') || 'name@example.com, weitere@example.com'}
                          className="flex-1 px-2 py-1.5 text-[12px] bg-surface-card border border-line rounded-button text-content placeholder:text-content-tertiary focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                        <button
                          type="button"
                          onClick={() => setComposer(c => ({ ...c, bcc_emails: '', show_bcc: false }))}
                          className="text-content-tertiary hover:text-red-500"
                          aria-label={t('common.remove') || 'Entfernen'}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                  <input
                    type="text"
                    value={composer.subject}
                    onChange={e => setComposer(c => ({ ...c, subject: e.target.value }))}
                    placeholder={t('claims.composer.subjectPlaceholder') || 'Betreff…'}
                    className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content placeholder:text-content-tertiary focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  {/* #1161 — RichTextEditor + Drag&Drop für Datei-Upload */}
                  <div
                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); if (!composerDragOver) setComposerDragOver(true); }}
                    onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setComposerDragOver(false); }}
                    onDrop={e => {
                      e.preventDefault(); e.stopPropagation(); setComposerDragOver(false);
                      const files = Array.from(e.dataTransfer?.files || []);
                      if (!files.length) return;
                      setComposerAttachments(prev => [
                        ...prev,
                        ...files.map(f => ({ file_name: f.name, mime_type: f.type, file_size: f.size, blob: f })),
                      ]);
                    }}
                    className={`relative rounded-button border ${composerDragOver ? 'border-primary-500 border-dashed bg-primary-500/5' : 'border-transparent'}`}
                  >
                    <RichTextEditor
                      value={composer.body}
                      onChange={(html) => setComposer(c => ({ ...c, body: html }))}
                      placeholder={t('claims.composer.bodyPlaceholder') || 'Nachricht eingeben…'}
                      minHeight="180px"
                      maxHeight="420px"
                    />
                    {composerDragOver && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none rounded-button bg-primary-500/10 border-2 border-dashed border-primary-500">
                        <span className="text-[12px] text-primary-700 dark:text-primary-300 font-medium">
                          📥 {t('claims.composer.dropHere') || 'Dateien hier ablegen'}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* #1224 P4 — Dokument-Picker (nur claim_documents, #1760: Nachrichten-Anhänge ausgeschlossen) */}
                  {(claim.documents || []).filter(d => d.source !== 'message').length > 0 && (
                    <div className="border border-line rounded-button">
                      <button
                        type="button"
                        onClick={() => setComposerDocPickerOpen(o => !o)}
                        className="w-full flex items-center justify-between px-3 py-2 text-[12px] text-content hover:bg-surface-hover"
                      >
                        <span>
                          📎 {t('claims.composer.documents') || 'Dokumente aus Claim anhängen'}
                          {composerDocPicks.length > 0 && (
                            <span className="ml-1.5 px-1.5 py-0.5 rounded-badge text-[10px] bg-primary-500/10 text-primary-600">
                              {composerDocPicks.length}
                            </span>
                          )}
                        </span>
                        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${composerDocPickerOpen ? 'rotate-90' : ''}`} />
                      </button>
                      {composerDocPickerOpen && (
                        <div className="px-3 py-2 border-t border-line max-h-48 overflow-y-auto space-y-1">
                          {(claim.documents || []).filter(d => d.source !== 'message').map(doc => {
                            const isPicked = composerDocPicks.includes(doc.id);
                            return (
                              <label key={doc.id} className="flex items-center gap-2 text-[12px] text-content cursor-pointer hover:bg-surface-hover px-1 py-1 rounded">
                                <input
                                  type="checkbox"
                                  checked={isPicked}
                                  onChange={() => setComposerDocPicks(prev => isPicked ? prev.filter(x => x !== doc.id) : [...prev, doc.id])}
                                  className="w-3.5 h-3.5 rounded border-line text-primary-600 focus:ring-primary-500"
                                />
                                <span className="truncate flex-1">{doc.file_name}</span>
                                <span className="text-[10px] text-content-tertiary">{doc.file_size ? `${(doc.file_size / 1024).toFixed(0)} KB` : ''}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Attachments preview */}
                  {composerAttachments.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {composerAttachments.map((att, idx) => (
                        <div key={idx} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] bg-surface-inset border border-line text-content">
                          <span className="truncate max-w-[140px]">{att.file_name}</span>
                          <span className="text-content-tertiary">({(att.file_size / 1024).toFixed(0)} KB)</span>
                          <button onClick={() => handleComposerRemoveAttachment(idx)} className="hover:text-red-500">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Action bar */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={handleComposerAiPreview}
                      disabled={composerAiLoading}
                      className="px-3 py-1.5 text-[12px] rounded-button border border-line bg-surface-card hover:bg-surface-hover text-content disabled:opacity-50"
                    >
                      {composerAiLoading ? (t('common.loading') || 'Lade…') : `✨ ${t('claims.composer.aiSuggest') || 'KI-Vorschlag'}`}
                    </button>
                    <div className="relative">
                      <button
                        onClick={() => setComposerTemplateOpen(o => !o)}
                        className="px-3 py-1.5 text-[12px] rounded-button border border-line bg-surface-card hover:bg-surface-hover text-content"
                      >
                        📄 {t('claims.composer.template') || 'Template'}
                      </button>
                      {composerTemplateOpen && composerTemplates.length > 0 && (
                        <div className="absolute z-10 mt-1 bg-surface-card border border-line-card rounded-card shadow-lg min-w-[240px]">
                          {composerTemplates.map(tpl => (
                            <button
                              key={tpl.id}
                              onClick={() => handleComposerTemplatePick(tpl)}
                              className="w-full text-left px-3 py-2 text-[12px] hover:bg-surface-hover text-content border-b border-line last:border-0"
                            >
                              <div className="font-medium">{tpl.name || tpl.funktion}</div>
                              <div className="text-[10px] text-content-tertiary truncate">{tpl.betreff}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <input ref={composerFileRef} type="file" multiple className="hidden" onChange={handleComposerAttach} />
                    <button
                      onClick={() => composerFileRef.current?.click()}
                      className="px-3 py-1.5 text-[12px] rounded-button border border-line bg-surface-card hover:bg-surface-hover text-content"
                    >
                      📎 {t('claims.composer.attach') || 'Anhang'}
                    </button>
                    <div className="ml-auto">
                      <Button size="sm" icon={Send} loading={composerSending} onClick={handleComposerSend}
                        disabled={
                          !composer.subject.trim() || !composer.body.trim() ||
                          (composer.channel === 'carrier' && !claim.submission_email) ||
                          (composer.channel === 'dritte' && !composer.to_address.trim())
                        }>
                        {t('claims.composer.send') || 'Senden'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Inline-Notiz (optional, oldskool — bleibt als Sub-Option) */}
                <div className="mt-4 pt-3 border-t border-line flex gap-2">
                  <input
                    type="text"
                    value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleAddNote()}
                    placeholder={t('claims.detail.addNote') + '...'}
                    className="flex-1 px-3 py-2 text-[12px] bg-surface-card border border-line rounded-button text-content placeholder:text-content-tertiary focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <Button size="sm" variant="secondary" icon={MessageSquare} loading={sendingNote} onClick={handleAddNote} disabled={!noteText.trim()}>
                    {t('claims.detail.addNote')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ Linked Cases Tab (#1246) ═══ */}
        {activeTab === 'linked-cases' && (
          <LinkedEntityList
            rows={linkedCases}
            loading={linkedCasesLoading}
            entityType="case"
            onOpen={(row, url) => navigate(url)}
            onUnlink={handleUnlinkCase}
            onLinkClick={() => setLinkCaseModalOpen(true)}
            unlinking={unlinkingCase}
          />
        )}
      </div>

      <LinkEntityModal
        isOpen={linkCaseModalOpen}
        onClose={() => setLinkCaseModalOpen(false)}
        mode="pick-case"
        sourceId={claim?.id}
        customerId={claim?.customer_id}
        onLinked={() => { fetchLinkedCases(); fetchClaim(); }}
      />

      {/* ═══ Forward Dialog (#1250) ═══ */}
      {forwardModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setForwardModal(f => ({ ...f, open: false }))}>
          <div className="bg-surface-card border border-line-card rounded-card p-6 w-full max-w-xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[15px] font-bold text-content">⇨ {t('claims.forward.title') || 'Nachricht weiterleiten'}</h3>
              <button onClick={() => setForwardModal(f => ({ ...f, open: false }))} className="text-content-tertiary hover:text-content"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-[12px]">
                <span className="w-16 text-content-tertiary">{t('claims.composer.channelLabel') || 'Channel:'}</span>
                <select
                  value={forwardModal.channel}
                  onChange={e => setForwardModal(f => ({ ...f, channel: e.target.value, to_address: '', to_name: '' }))}
                  className="px-2 py-1 text-[12px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="kunde">👤 {t('claims.channels.kunde')   || 'Kunde'}</option>
                  <option value="carrier">🚚 {t('claims.channels.carrier') || 'Carrier'}</option>
                  <option value="dritte">🏢 {t('claims.channels.dritte')  || 'Dritte'}</option>
                </select>
              </div>
              <div className="flex items-center gap-2 text-[12px]">
                <span className="w-16 text-content-tertiary">{t('claims.composer.to') || 'An:'}</span>
                <input
                  type="text"
                  value={forwardModal.to_address}
                  onChange={e => setForwardModal(f => ({ ...f, to_address: e.target.value }))}
                  placeholder={t('claims.composer.recipientPlaceholder') || 'empfaenger@example.com'}
                  className="flex-1 px-2 py-1.5 text-[12px] bg-surface-card border border-line rounded-button text-content placeholder:text-content-tertiary focus:outline-none focus:ring-1 focus:ring-primary-500 font-mono"
                />
                <div className="flex items-center gap-2">
                  {!forwardModal.show_cc && (
                    <button type="button" onClick={() => setForwardModal(f => ({ ...f, show_cc: true }))} className="text-[11px] text-primary-600 hover:underline whitespace-nowrap">
                      + {t('claims.composer.addCc') || 'CC hinzufügen'}
                    </button>
                  )}
                  {!forwardModal.show_bcc && (
                    <button type="button" onClick={() => setForwardModal(f => ({ ...f, show_bcc: true }))} className="text-[11px] text-primary-600 hover:underline whitespace-nowrap">
                      + {t('claims.composer.addBcc') || 'BCC hinzufügen'}
                    </button>
                  )}
                </div>
              </div>
              {forwardModal.show_cc && (
                <div className="flex items-center gap-2 text-[12px]">
                  <span className="w-16 text-content-tertiary">{t('claims.composer.cc') || 'CC:'}</span>
                  <input
                    type="text"
                    value={forwardModal.cc_emails}
                    onChange={e => setForwardModal(f => ({ ...f, cc_emails: e.target.value }))}
                    placeholder={t('claims.composer.ccPlaceholder') || 'name@example.com, weitere@example.com'}
                    className="flex-1 px-2 py-1.5 text-[12px] bg-surface-card border border-line rounded-button text-content placeholder:text-content-tertiary focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <button type="button" onClick={() => setForwardModal(f => ({ ...f, cc_emails: '', show_cc: false }))} className="text-content-tertiary hover:text-red-500">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              {forwardModal.show_bcc && (
                <div className="flex items-center gap-2 text-[12px]">
                  <span className="w-16 text-content-tertiary">{t('claims.composer.bcc') || 'BCC:'}</span>
                  <input
                    type="text"
                    value={forwardModal.bcc_emails}
                    onChange={e => setForwardModal(f => ({ ...f, bcc_emails: e.target.value }))}
                    placeholder={t('claims.composer.bccPlaceholder') || 'name@example.com, weitere@example.com'}
                    className="flex-1 px-2 py-1.5 text-[12px] bg-surface-card border border-line rounded-button text-content placeholder:text-content-tertiary focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <button type="button" onClick={() => setForwardModal(f => ({ ...f, bcc_emails: '', show_bcc: false }))} className="text-content-tertiary hover:text-red-500">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2 text-[12px]">
                <span className="w-16 text-content-tertiary">{t('claims.composer.subject') || 'Betreff:'}</span>
                <input
                  type="text"
                  value={forwardModal.subject}
                  onChange={e => setForwardModal(f => ({ ...f, subject: e.target.value }))}
                  className="flex-1 px-2 py-1.5 text-[12px] bg-surface-card border border-line rounded-button text-content placeholder:text-content-tertiary focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              {/* #1161 — RichTextEditor + Drag&Drop */}
              <div
                onDragOver={e => { e.preventDefault(); e.stopPropagation(); if (!forwardDragOver) setForwardDragOver(true); }}
                onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setForwardDragOver(false); }}
                onDrop={e => {
                  e.preventDefault(); e.stopPropagation(); setForwardDragOver(false);
                  const files = Array.from(e.dataTransfer?.files || []);
                  if (!files.length) return;
                  setForwardAttachments(prev => [
                    ...prev,
                    ...files.map(f => ({ file_name: f.name, mime_type: f.type, file_size: f.size, blob: f })),
                  ]);
                }}
                className={`relative rounded-button border ${forwardDragOver ? 'border-primary-500 border-dashed bg-primary-500/5' : 'border-transparent'}`}
              >
                <RichTextEditor
                  value={forwardModal.body}
                  onChange={(html) => setForwardModal(f => ({ ...f, body: html }))}
                  placeholder={t('claims.composer.bodyPlaceholder') || 'Nachricht eingeben…'}
                  minHeight="240px"
                  maxHeight="420px"
                />
                {forwardDragOver && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none rounded-button bg-primary-500/10 border-2 border-dashed border-primary-500">
                    <span className="text-[12px] text-primary-700 dark:text-primary-300 font-medium">
                      📥 {t('claims.composer.dropHere') || 'Dateien hier ablegen'}
                    </span>
                  </div>
                )}
              </div>
              {/* Attachments preview */}
              {forwardAttachments.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {forwardAttachments.map((att, idx) => (
                    <div key={idx} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] bg-surface-inset border border-line text-content">
                      <span className="truncate max-w-[140px]">{att.file_name}</span>
                      <span className="text-content-tertiary">({(att.file_size / 1024).toFixed(0)} KB)</span>
                      <button onClick={() => setForwardAttachments(prev => prev.filter((_, i) => i !== idx))} className="hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <input ref={forwardFileRef} type="file" multiple className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (!files.length) return;
                    setForwardAttachments(prev => [
                      ...prev,
                      ...files.map(f => ({ file_name: f.name, mime_type: f.type, file_size: f.size, blob: f })),
                    ]);
                    if (forwardFileRef.current) forwardFileRef.current.value = '';
                  }}
                />
                <button
                  type="button"
                  onClick={() => forwardFileRef.current?.click()}
                  className="px-3 py-1.5 text-[12px] rounded-button border border-line bg-surface-card hover:bg-surface-hover text-content"
                >
                  📎 {t('claims.composer.attach') || 'Anhang'}
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="secondary" size="sm" onClick={() => setForwardModal(f => ({ ...f, open: false }))}>{t('common.cancel') || 'Abbrechen'}</Button>
              <Button size="sm" icon={Send} onClick={handleForwardSend}
                disabled={!forwardModal.subject.trim() || !forwardModal.body.trim() || !forwardModal.to_address.trim()}>
                {t('claims.forward.send') || 'Weiterleiten'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Status Change Modal ═══ */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowStatusModal(false)}>
          <div className="bg-surface-card border border-line-card rounded-card p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[15px] font-bold text-content">{t('claims.detail.changeStatus')}</h3>
              <button onClick={() => setShowStatusModal(false)} className="text-content-tertiary hover:text-content"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[12px] text-content-secondary font-medium">{t('claims.detail.currentStatus')}</label>
                <div className="mt-1"><StatusBadge status={claim.status} t={t} /></div>
              </div>
              <div>
                <label className="text-[12px] text-content-secondary font-medium">Neuer Status</label>
                <select
                  value={statusForm.status}
                  onChange={e => setStatusForm(f => ({ ...f, status: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  {transitions.map(s => (
                    <option key={s} value={s}>{t(`claims.status.${s}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[12px] text-content-secondary font-medium">Kommentar</label>
                <textarea
                  value={statusForm.description}
                  onChange={e => setStatusForm(f => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="mt-1 w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content resize-none focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="Optional..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="secondary" size="sm" onClick={() => setShowStatusModal(false)}>Abbrechen</Button>
              <Button size="sm" loading={saving} onClick={handleStatusChange}>{t('common.save') || 'Speichern'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Resolve Modal ═══ */}
      {showResolveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowResolveModal(false)}>
          <div className="bg-surface-card border border-line-card rounded-card p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[15px] font-bold text-content">{t('claims.detail.resolveModal.title')}</h3>
              <button onClick={() => setShowResolveModal(false)} className="text-content-tertiary hover:text-content"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[12px] text-content-secondary font-medium">{t('claims.detail.resolveModal.type')}</label>
                <select
                  value={resolveForm.resolution_type}
                  onChange={e => setResolveForm(f => ({ ...f, resolution_type: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  {['refund', 'goodwill', 'rejected', 'delivered', 'returned'].map(rt => (
                    <option key={rt} value={rt}>{t(`claims.resolution.${rt}`)}</option>
                  ))}
                </select>
              </div>
              {/* #1218 C2: Empfänger-Toggle nur bei refund */}
              {resolveForm.resolution_type === 'refund' && (
                <div>
                  <label className="text-[12px] text-content-secondary font-medium">{t('claims.detail.resolveModal.recipient')}</label>
                  <div className="mt-1 grid grid-cols-2 gap-2">
                    {['weship', 'kunde'].map(rec => (
                      <button
                        key={rec}
                        type="button"
                        onClick={() => setResolveForm(f => ({ ...f, resolution_recipient: rec }))}
                        className={`px-3 py-2 text-[12px] rounded-button border transition-colors ${
                          resolveForm.resolution_recipient === rec
                            ? 'border-primary-500 bg-primary-500/10 text-primary-600 font-medium'
                            : 'border-line text-content-secondary hover:border-content-tertiary'
                        }`}
                      >
                        {t(`claims.detail.resolveModal.recipient_${rec}`)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {['refund', 'goodwill'].includes(resolveForm.resolution_type) && (
                <div>
                  <label className="text-[12px] text-content-secondary font-medium">{t('claims.detail.resolveModal.amount')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={resolveForm.resolution_amount}
                    onChange={e => setResolveForm(f => ({ ...f, resolution_amount: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                    placeholder="0,00"
                  />
                </div>
              )}
              <div>
                <label className="text-[12px] text-content-secondary font-medium">{t('claims.detail.resolveModal.notes')}</label>
                <textarea
                  value={resolveForm.resolution_notes}
                  onChange={e => setResolveForm(f => ({ ...f, resolution_notes: e.target.value }))}
                  rows={3}
                  className="mt-1 w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content resize-none focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="secondary" size="sm" onClick={() => setShowResolveModal(false)}>Abbrechen</Button>
              <Button size="sm" icon={CheckCircle2} loading={saving} onClick={handleResolve}>{t('claims.detail.resolve')}</Button>
            </div>
          </div>
        </div>
      )}

      {/* #1236 ═══ Eskalations-Modal (Klärung → Schadensfall) ═══ */}
      {showEscalateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowEscalateModal(false)}>
          <div className="bg-surface-card rounded-card border border-line-card shadow-lg w-[480px] max-w-[90vw] p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[14px] font-bold text-content">{t('claims.detail.escalateModal.title') || 'Klärung zum Schadensfall eskalieren'}</h3>
              <button onClick={() => setShowEscalateModal(false)} className="text-content-tertiary hover:text-content"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-[12px] text-content-tertiary mb-4">
              {t('claims.detail.escalateModal.hint') || 'Damit wechselt der Status auf „Eingereicht" und der Schadensfall-Workflow startet. Auto-Submit zum Carrier läuft, falls konfiguriert.'}
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-[12px] text-content-secondary font-medium">{t('claims.claimType') || 'Fall-Typ'}</label>
                <select
                  value={escalateForm.claim_type}
                  onChange={e => setEscalateForm(f => ({ ...f, claim_type: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">—</option>
                  <option value="damage">{t('claims.damage') || 'Schadensmeldung'}</option>
                  <option value="investigation">{t('claims.investigation') || 'Nachforschung'}</option>
                  <option value="content_missing">{t('claims.contentMissing') || 'Inhalt fehlt'}</option>
                </select>
              </div>
              <div>
                <label className="text-[12px] text-content-secondary font-medium">{t('claims.carrier')}</label>
                <select
                  value={escalateForm.carrier_id}
                  onChange={e => setEscalateForm(f => ({ ...f, carrier_id: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">—</option>
                  {carriers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[12px] text-content-secondary font-medium">{t('claims.fields.lager') || 'Lager'}</label>
                <input
                  type="text"
                  value={escalateForm.lager}
                  onChange={e => setEscalateForm(f => ({ ...f, lager: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="text-[12px] text-content-secondary font-medium">{t('claims.detail.escalateModal.note') || 'Eskalations-Notiz'}</label>
                <textarea
                  value={escalateForm.escalation_note}
                  onChange={e => setEscalateForm(f => ({ ...f, escalation_note: e.target.value }))}
                  rows={3}
                  className="mt-1 w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content resize-none focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder={t('claims.detail.escalateModal.notePlaceholder') || 'Warum wird eskaliert? (optional, landet in der Timeline)'}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="secondary" size="sm" onClick={() => setShowEscalateModal(false)}>{t('common.cancel') || 'Abbrechen'}</Button>
              <Button size="sm" icon={Send} loading={saving} onClick={handleEscalate}>{t('claims.detail.escalateToClaim') || 'Eskalieren'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* #1236 ═══ Klärung-Schließen-Modal ═══ */}
      {showCloseClarificationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowCloseClarificationModal(false)}>
          <div className="bg-surface-card rounded-card border border-line-card shadow-lg w-[440px] max-w-[90vw] p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[14px] font-bold text-content">{t('claims.detail.closeClarificationModal.title') || 'Klärung als erledigt schließen'}</h3>
              <button onClick={() => setShowCloseClarificationModal(false)} className="text-content-tertiary hover:text-content"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-[12px] text-content-tertiary mb-4">
              {t('claims.detail.closeClarificationModal.hint') || 'Wähle den Ausgang der Klärung. Der Case wird ohne Carrier-Eskalation geschlossen.'}
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-[12px] text-content-secondary font-medium">{t('claims.detail.closeClarificationModal.outcome') || 'Ausgang'}</label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  {['delivered', 'returned', 'no_action_needed', 'other'].map(o => (
                    <button
                      key={o}
                      type="button"
                      onClick={() => setCloseClarificationForm(f => ({ ...f, outcome: o }))}
                      className={`px-3 py-2 text-[12px] rounded-button border transition-colors ${
                        closeClarificationForm.outcome === o
                          ? 'border-primary-500 bg-primary-500/10 text-primary-600 font-medium'
                          : 'border-line text-content-secondary hover:border-content-tertiary'
                      }`}
                    >
                      {t(`claims.clarificationOutcome.${o}`) || o}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[12px] text-content-secondary font-medium">{t('claims.detail.closeClarificationModal.notes') || 'Notizen'}</label>
                <textarea
                  value={closeClarificationForm.notes}
                  onChange={e => setCloseClarificationForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  className="mt-1 w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content resize-none focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="secondary" size="sm" onClick={() => setShowCloseClarificationModal(false)}>{t('common.cancel') || 'Abbrechen'}</Button>
              <Button size="sm" icon={CheckCircle2} loading={saving} onClick={handleCloseClarification}>{t('claims.detail.closeClarification') || 'Klärung schließen'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Image Lightbox ═══ */}
      {lightboxIdx >= 0 && imageDocuments.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setLightboxIdx(-1)}>
          <button onClick={() => setLightboxIdx(-1)} className="absolute top-4 right-4 text-white/70 hover:text-white">
            <X className="w-6 h-6" />
          </button>
          {lightboxIdx > 0 && (
            <button
              onClick={e => { e.stopPropagation(); setLightboxIdx(i => i - 1); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          {lightboxIdx < imageDocuments.length - 1 && (
            <button
              onClick={e => { e.stopPropagation(); setLightboxIdx(i => i + 1); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
          <img
            src={docDownloadUrl(imageDocuments[lightboxIdx])}
            alt={imageDocuments[lightboxIdx].file_name}
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
            onClick={e => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-[12px]">
            {imageDocuments[lightboxIdx].file_name} ({lightboxIdx + 1}/{imageDocuments.length})
          </div>
        </div>
      )}

      {/* #1241 — Original-Mail Modal (Sandboxed Iframe) */}
      <MailOriginalModal
        open={originalModal.open}
        onClose={() => setOriginalModal({ open: false, html: '', subject: '' })}
        html={originalModal.html}
        subject={originalModal.subject}
      />
    </div>
  );
}
